import { Add, Edit } from '@mui/icons-material'
import {
  Box,
  Button,
  Card,
  CardContent,
  Grid,
  MenuItem,
  Chip as MuiChip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableFooter,
  TableHead,
  TablePagination,
  TableRow,
  TextField
} from '@mui/material'
import { AddVINDialog, ExplanationAccordion, SortableTableCell } from 'components'
import { ListBody } from 'components/list'
import { useLayout } from 'context'
import * as controllers from 'controllers'
import { VehicleDto } from 'dtos'
import { useLoadingState, useQuery } from 'hooks'
import { DEBOUNCE_DELAY } from 'localConstants'
import moment from 'moment'
import { enqueueSnackbar } from 'notistack'
import { VehicleToDoListParameters } from 'parameters'
import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { formatDate, useDebounce } from 'utils'

export default function NeedsIncorrectMileageResolutionToDoListDetails() {
  const layout = useLayout()
  const navigate = useNavigate()
  const query = useQuery()

  const [addVINDialogOpen, setAddVINDialogOpen] = useState<boolean>(false)
  const [filterField, setFilterField] = useState<string>('')
  const [filterValue, setFilterValue] = useState<string>('')
  const [filterValueFrom, setFilterValueFrom] = useState<string>('')
  const [filterValueTo, setFilterValueTo] = useState<string>('')
  const [loadingState, setLoadingState] = useLoadingState({ isGettingVehicles: false, isCreatingVehicle: false })
  const [parameters, setParameters] = useState<VehicleToDoListParameters>({
    page: 0,
    pageSize: 10,
    orderBy: 'needsIncorrectMileageResolutionDate,clusterConversionCompletedDate',
    order: 'asc'
  })
  const [totalCount, setTotalCount] = useState<number>(0)
  const [vehicles, setVehicles] = useState<VehicleDto[]>()

  const onChangeSortByDirection = (orderBy: string) => {
    setParameters(prev => ({
      ...parameters,
      orderBy,
      order: prev.sortBy === orderBy && prev.order === 'asc' ? 'desc' : 'asc'
    }))
  }

  const getNeedsIncorrectMileageResolutionToDoList = useDebounce((parameters: VehicleToDoListParameters) => {
    setLoadingState('isGettingVehicles', true)
    controllers.toDoLists
      .getNeedsIncorrectMileageResolutionToDoList(parameters)
      .then(response => {
        setTotalCount(response.totalCount ?? 0)
        setVehicles(response.value)
      })
      .finally(() => {
        setLoadingState('isGettingVehicles', false)
      })
  }, DEBOUNCE_DELAY)

  useEffect(() => {
    layout.setTitle('Needs Incorrect Mileage Resolution To Do List')

    return () => {
      layout.setTitle('')
    }
  }, [layout])

  useEffect(() => {
    layout.setIsLayoutLoading(loadingState.isGettingVehicles)
  }, [layout, loadingState.isGettingVehicles])

  useEffect(() => {
    getNeedsIncorrectMileageResolutionToDoList(parameters)
  }, [getNeedsIncorrectMileageResolutionToDoList, parameters])

  const importBranchGuid = query.get('importBranchGuid') ?? undefined
  useEffect(() => {
    setParameters({ ...parameters, importBranchGuid })
  }, [importBranchGuid])

  return (
    <>
      <AddVINDialog
        initialValues={new VehicleDto()}
        isLoading={loadingState.isCreatingVehicle}
        onClose={() => {
          setAddVINDialogOpen(false)
        }}
        onSave={values => {
          setLoadingState('isCreatingVehicle', true)
          controllers
            .createVehicle(values)
            .then(_ => {
              enqueueSnackbar('Vehicle Saved Successfully!', { variant: 'success' })
              setAddVINDialogOpen(false)
              getNeedsIncorrectMileageResolutionToDoList(parameters)
            })
            .finally(() => {
              setLoadingState('isCreatingVehicle', false)
            })
        }}
        open={addVINDialogOpen}
      />

      <Card>
        <CardContent>
          <Grid container spacing={2} alignItems='center'>
            <Grid item xs={12} container alignItems='center' justifyContent='space-between'>
              <Grid item xs={12} sm='auto' container spacing={1} alignItems='center'>
                <Grid item xs={12} sm='auto'>
                  <TextField
                    fullWidth
                    label='Filter Field'
                    onChange={e => {
                      setFilterField(e.target.value)
                      setFilterValue('')
                    }}
                    select
                    sx={{ minWidth: 200 }}
                    value={filterField || ''}
                  >
                    <MenuItem value='needsIncorrectMileageResolutionDate'>Needs Incorrect Mileage Resolution Date</MenuItem>

                    <MenuItem value='clusterConversionCompletedDate'>Cluster Conversion Completed Date</MenuItem>

                    <MenuItem value='additionalProgrammingCompletedDate'>Additional Programming Completed Date</MenuItem>

                    <MenuItem value='exporterBuyer'>Exporter/Buyer</MenuItem>

                    <MenuItem value='vin'>VIN</MenuItem>

                    <MenuItem value='year'>Year</MenuItem>

                    <MenuItem value='make'>Make</MenuItem>

                    <MenuItem value='model'>Model</MenuItem>

                    <MenuItem value='importerStockNumber'>Importer Stock #</MenuItem>
                  </TextField>
                </Grid>

                {['', 'exporterBuyer', 'vin', 'year', 'make', 'model', 'importerStockNumber'].includes(filterField) && (
                  <Grid item xs={12} sm='auto'>
                    <TextField
                      disabled={!filterField}
                      label='Filter Value'
                      onChange={e => {
                        setFilterValue(e.target.value)
                      }}
                      value={filterValue || ''}
                    />
                  </Grid>
                )}

                {filterField === 'needsIncorrectMileageResolutionDate' && (
                  <>
                    <Grid item xs={12} sm='auto'>
                      <TextField
                        InputLabelProps={{ shrink: true }}
                        InputProps={{ inputProps: { max: filterValueTo ?? undefined } }}
                        label='Filter Date From'
                        onChange={e => {
                          setFilterValueFrom(e.target.value)
                        }}
                        type='date'
                        value={filterValueFrom}
                      />
                    </Grid>

                    <Grid item xs={12} sm='auto'>
                      <TextField
                        InputLabelProps={{ shrink: true }}
                        InputProps={{ inputProps: { min: filterValueFrom ?? undefined } }}
                        label='Filter Date To'
                        onChange={e => {
                          setFilterValueTo(e.target.value)
                        }}
                        type='date'
                        value={filterValueTo}
                      />
                    </Grid>
                  </>
                )}

                {filterField === 'clusterConversionCompletedDate' && (
                  <>
                    <Grid item xs={12} sm='auto'>
                      <TextField
                        InputLabelProps={{ shrink: true }}
                        InputProps={{ inputProps: { max: filterValueTo ?? undefined } }}
                        label='Filter Date From'
                        onChange={e => {
                          setFilterValueFrom(e.target.value)
                        }}
                        type='date'
                        value={filterValueFrom}
                      />
                    </Grid>

                    <Grid item xs={12} sm='auto'>
                      <TextField
                        InputLabelProps={{ shrink: true }}
                        InputProps={{ inputProps: { min: filterValueFrom ?? undefined } }}
                        label='Filter Date To'
                        onChange={e => {
                          setFilterValueTo(e.target.value)
                        }}
                        type='date'
                        value={filterValueTo}
                      />
                    </Grid>
                  </>
                )}

                {filterField === 'additionalProgrammingCompletedDate' && (
                  <>
                    <Grid item xs={12} sm='auto'>
                      <TextField
                        InputLabelProps={{ shrink: true }}
                        InputProps={{ inputProps: { max: filterValueTo ?? undefined } }}
                        label='Filter Date From'
                        onChange={e => {
                          setFilterValueFrom(e.target.value)
                        }}
                        type='date'
                        value={filterValueFrom}
                      />
                    </Grid>

                    <Grid item xs={12} sm='auto'>
                      <TextField
                        InputLabelProps={{ shrink: true }}
                        InputProps={{ inputProps: { min: filterValueFrom ?? undefined } }}
                        label='Filter Date To'
                        onChange={e => {
                          setFilterValueTo(e.target.value)
                        }}
                        type='date'
                        value={filterValueTo}
                      />
                    </Grid>
                  </>
                )}

                <Grid item xs={12} sm='auto'>
                  <Button
                    color='inherit'
                    disabled={
                      !filterField ||
                      (filterField === 'needsIncorrectMileageResolutionDate'
                        ? !filterValueFrom || !filterValueTo
                        : !filterValue) ||
                      (filterField === 'clusterConversionCompletedDate' ? !filterValueFrom || !filterValueTo : !filterValue) ||
                      (filterField === 'additionalProgrammingCompletedDate' ? !filterValueFrom || !filterValueTo : !filterValue)
                    }
                    variant='contained'
                    onClick={() => {
                      if (filterField === 'needsIncorrectMileageResolutionDate') {
                        setParameters({
                          ...parameters,
                          needsIncorrectMileageResolutionDateFrom: filterValueFrom,
                          needsIncorrectMileageResolutionDateTo: filterValueTo
                        })
                      } else if (filterField === 'clusterConversionCompletedDate') {
                        setParameters({
                          ...parameters,
                          clusterConversionCompletedDateFrom: filterValueFrom,
                          clusterConversionCompletedDateTo: filterValueTo
                        })
                      } else if (filterField === 'additionalProgrammingCompletedDate') {
                        setParameters({
                          ...parameters,
                          additionalProgrammingCompletedDateFrom: filterValueFrom,
                          additionalProgrammingCompletedDateTo: filterValueTo
                        })
                      } else if (filterField === 'exporterBuyer') {
                        setParameters({
                          ...parameters,
                          exporter: filterValue,
                          buyer: filterValue
                        })
                      } else {
                        setParameters({
                          ...parameters,
                          [filterField]: filterValue
                        })
                      }

                      setFilterField('')
                      setFilterValue('')
                      setFilterValueFrom('')
                      setFilterValueTo('')
                    }}
                  >
                    APPLY FILTER
                  </Button>
                </Grid>

                <Grid item xs={12} sm='auto'>
                  <Button
                    color='secondary'
                    disabled={
                      !Boolean(
                        parameters.needsIncorrectMileageResolutionDateFrom ||
                          parameters.needsIncorrectMileageResolutionDateTo ||
                          parameters.clusterConversionCompletedDateFrom ||
                          parameters.clusterConversionCompletedDateTo ||
                          parameters.additionalProgrammingCompletedDateFrom ||
                          parameters.additionalProgrammingCompletedDateTo ||
                          parameters.buyer ||
                          parameters.exporter ||
                          parameters.vin ||
                          parameters.year ||
                          parameters.make ||
                          parameters.model ||
                          parameters.importerStockNumber
                      )
                    }
                    variant='text'
                    onClick={() => {
                      setParameters({
                        ...parameters,
                        needsIncorrectMileageResolutionDateFrom: undefined,
                        needsIncorrectMileageResolutionDateTo: undefined,
                        clusterConversionCompletedDateFrom: undefined,
                        clusterConversionCompletedDateTo: undefined,
                        additionalProgrammingCompletedDateFrom: undefined,
                        additionalProgrammingCompletedDateTo: undefined,
                        buyer: undefined,
                        exporter: undefined,
                        vin: undefined,
                        year: undefined,
                        make: undefined,
                        model: undefined,
                        importerStockNumber: undefined
                      })
                    }}
                  >
                    RESET FILTERS
                  </Button>
                </Grid>
              </Grid>

              <Grid item>
                <Button
                  color='primary'
                  endIcon={<Add />}
                  onClick={() => {
                    setAddVINDialogOpen(true)
                  }}
                  variant='text'
                >
                  ADD VIN
                </Button>
              </Grid>
            </Grid>

            {parameters.needsIncorrectMileageResolutionDateFrom && parameters.needsIncorrectMileageResolutionDateTo && (
              <Grid item xs={12} sm='auto'>
                <MuiChip
                  color='secondary'
                  label={`Needs Incorrect Mileage Resolution Date from "${moment(
                    parameters.needsIncorrectMileageResolutionDateFrom
                  ).format('M/D/yyyy')}" to "${moment(parameters.needsIncorrectMileageResolutionDateTo).format('M/D/yyyy')}"`}
                  onDelete={() => {
                    setParameters({
                      ...parameters,
                      needsIncorrectMileageResolutionDateFrom: undefined,
                      needsIncorrectMileageResolutionDateTo: undefined
                    })
                  }}
                  variant='outlined'
                />
              </Grid>
            )}

            {parameters.clusterConversionCompletedDateFrom && parameters.clusterConversionCompletedDateTo && (
              <Grid item xs={12} sm='auto'>
                <MuiChip
                  color='secondary'
                  label={`Cluster Conversion Completed Date from "${moment(
                    parameters.clusterConversionCompletedDateFrom
                  ).format('M/D/yyyy')}" to "${moment(parameters.clusterConversionCompletedDateTo).format('M/D/yyyy')}"`}
                  onDelete={() => {
                    setParameters({
                      ...parameters,
                      clusterConversionCompletedDateFrom: undefined,
                      clusterConversionCompletedDateTo: undefined
                    })
                  }}
                  variant='outlined'
                />
              </Grid>
            )}

            {parameters.additionalProgrammingCompletedDateFrom && parameters.additionalProgrammingCompletedDateTo && (
              <Grid item xs={12} sm='auto'>
                <MuiChip
                  color='secondary'
                  label={`Additional Programming Completed Date from "${moment(
                    parameters.additionalProgrammingCompletedDateFrom
                  ).format('M/D/yyyy')}" to "${moment(parameters.additionalProgrammingCompletedDateTo).format('M/D/yyyy')}"`}
                  onDelete={() => {
                    setParameters({
                      ...parameters,
                      additionalProgrammingCompletedDateFrom: undefined,
                      additionalProgrammingCompletedDateTo: undefined
                    })
                  }}
                  variant='outlined'
                />
              </Grid>
            )}

            {(parameters.exporter || parameters.buyer) && (
              <Grid item xs={12} sm='auto'>
                <MuiChip
                  color='secondary'
                  label={`Exporter/Buyer contains "${parameters.exporter || parameters.buyer}"`}
                  onDelete={() => {
                    setParameters({
                      ...parameters,
                      exporter: undefined,
                      buyer: undefined
                    })
                  }}
                  variant='outlined'
                />
              </Grid>
            )}

            {parameters.vin && (
              <Grid item xs={12} sm='auto'>
                <MuiChip
                  color='secondary'
                  label={`VIN Contains "${parameters.vin}"`}
                  onDelete={() => {
                    setParameters({
                      ...parameters,
                      vin: undefined
                    })
                  }}
                  variant='outlined'
                />
              </Grid>
            )}

            {parameters.year && (
              <Grid item xs={12} sm='auto'>
                <MuiChip
                  color='secondary'
                  label={`Year Contains "${parameters.year}"`}
                  onDelete={() => {
                    setParameters({
                      ...parameters,
                      year: undefined
                    })
                  }}
                  variant='outlined'
                />
              </Grid>
            )}

            {parameters.make && (
              <Grid item xs={12} sm='auto'>
                <MuiChip
                  color='secondary'
                  label={`Make Contains "${parameters.make}"`}
                  onDelete={() => {
                    setParameters({
                      ...parameters,
                      make: undefined
                    })
                  }}
                  variant='outlined'
                />
              </Grid>
            )}

            {parameters.model && (
              <Grid item xs={12} sm='auto'>
                <MuiChip
                  color='secondary'
                  label={`Model Contains "${parameters.model}"`}
                  onDelete={() => {
                    setParameters({
                      ...parameters,
                      model: undefined
                    })
                  }}
                  variant='outlined'
                />
              </Grid>
            )}

            {parameters.importerStockNumber && (
              <Grid item xs={12} sm='auto'>
                <MuiChip
                  color='secondary'
                  label={`Importer Stock# Contains "${parameters.importerStockNumber}"`}
                  onDelete={() => {
                    setParameters({
                      ...parameters,
                      importerStockNumber: undefined
                    })
                  }}
                  variant='outlined'
                />
              </Grid>
            )}

            <Grid item xs={12}>
              <ListBody>
                <Box sx={{ width: '100%', overflowX: 'auto' }}>
                  <Table>
                    <TableContainer>
                      <Table>
                        <TableHead>
                          <TableRow>
                            <TableCell padding='checkbox'>
                              {/* <Checkbox color='primary' checked={false} onChange={() => {}} /> */}
                            </TableCell>

                            <SortableTableCell
                              onChangeSortByDirection={onChangeSortByDirection}
                              propertyName='needsIncorrectMileageResolutionDate'
                              sortBy={parameters.orderBy}
                              sortDirection={parameters.order}
                            >
                              Needs Incorrect Mileage Resolution
                            </SortableTableCell>

                            <SortableTableCell
                              onChangeSortByDirection={onChangeSortByDirection}
                              propertyName='clusterConversionCompletedDate'
                              sortBy={parameters.orderBy}
                              sortDirection={parameters.order}
                            >
                              Cluster Conversion Completed
                            </SortableTableCell>

                            <SortableTableCell
                              onChangeSortByDirection={onChangeSortByDirection}
                              propertyName='additionalProgrammingCompletedDate'
                              sortBy={parameters.orderBy}
                              sortDirection={parameters.order}
                            >
                              Additional Programming Completed
                            </SortableTableCell>

                            <SortableTableCell
                              onChangeSortByDirection={onChangeSortByDirection}
                              propertyName='exporterBuyer'
                              sortBy={parameters.orderBy}
                              sortDirection={parameters.order}
                            >
                              Exporter/Buyer
                            </SortableTableCell>

                            <SortableTableCell
                              onChangeSortByDirection={onChangeSortByDirection}
                              propertyName='vin'
                              sortBy={parameters.orderBy}
                              sortDirection={parameters.order}
                            >
                              VIN
                            </SortableTableCell>

                            <SortableTableCell
                              onChangeSortByDirection={onChangeSortByDirection}
                              propertyName='year'
                              sortBy={parameters.orderBy}
                              sortDirection={parameters.order}
                            >
                              Year
                            </SortableTableCell>

                            <SortableTableCell
                              onChangeSortByDirection={onChangeSortByDirection}
                              propertyName='make'
                              sortBy={parameters.orderBy}
                              sortDirection={parameters.order}
                            >
                              Make
                            </SortableTableCell>

                            <SortableTableCell
                              onChangeSortByDirection={onChangeSortByDirection}
                              propertyName='model'
                              sortBy={parameters.orderBy}
                              sortDirection={parameters.order}
                            >
                              Model
                            </SortableTableCell>

                            <SortableTableCell
                              onChangeSortByDirection={onChangeSortByDirection}
                              propertyName='importerStockNumber'
                              sortBy={parameters.orderBy}
                              sortDirection={parameters.order}
                            >
                              Importer Stock #
                            </SortableTableCell>

                            <TableCell padding='checkbox'></TableCell>
                          </TableRow>
                        </TableHead>

                        <TableBody>
                          {vehicles?.map((vehicle, index) => (
                            <TableRow key={vehicle.vin} sx={{ backgroundColor: index % 2 === 0 ? '#FCFCFD' : 'white' }}>
                              <TableCell padding='checkbox' sx={{ fontSize: '7.99pt' }}></TableCell>

                              <TableCell
                                sx={theme => ({
                                  color:
                                    moment().diff(moment(formatDate(vehicle.needsIncorrectMileageResolutionDate)), 'days') >= 2
                                      ? theme.palette.error.main
                                      : 'inherit',
                                  fontWeight:
                                    moment().diff(moment(formatDate(vehicle.needsIncorrectMileageResolutionDate)), 'days') >= 2
                                      ? 'bold'
                                      : 'inherit'
                                })}
                              >
                                {formatDate(vehicle.needsIncorrectMileageResolutionDate)}
                              </TableCell>

                              <TableCell
                                sx={theme => ({
                                  color:
                                    moment().diff(moment(formatDate(vehicle.clusterConversionCompletedDate)), 'days') >= 4
                                      ? theme.palette.error.main
                                      : 'inherit',
                                  fontWeight:
                                    moment().diff(moment(formatDate(vehicle.clusterConversionCompletedDate)), 'days') >= 4
                                      ? 'bold'
                                      : 'inherit'
                                })}
                              >
                                {formatDate(vehicle.clusterConversionCompletedDate)}
                              </TableCell>

                              <TableCell
                                sx={theme => ({
                                  color:
                                    moment().diff(moment(formatDate(vehicle.additionalProgrammingCompletedDate)), 'days') >= 2
                                      ? theme.palette.error.main
                                      : 'inherit',
                                  fontWeight:
                                    moment().diff(moment(formatDate(vehicle.additionalProgrammingCompletedDate)), 'days') >= 2
                                      ? 'bold'
                                      : 'inherit'
                                })}
                              >
                                {formatDate(vehicle.additionalProgrammingCompletedDate)}
                              </TableCell>

                              <TableCell>
                                {vehicle?.buyerCompany?.name ? vehicle.buyerCompany.name : vehicle?.exporterCompany?.name}
                              </TableCell>

                              <TableCell>{vehicle.vin}</TableCell>

                              <TableCell>{vehicle.year}</TableCell>

                              <TableCell>{vehicle.make}</TableCell>

                              <TableCell>{vehicle.model}</TableCell>

                              <TableCell>{vehicle.importerStockNumber}</TableCell>

                              <TableCell padding='checkbox' size='small'>
                                <Button
                                  color='primary'
                                  endIcon={<Edit />}
                                  onClick={() => {
                                    navigate(`/vehicles/${vehicle.vehicleGuid}/cluster`)
                                  }}
                                  size='small'
                                  variant='text'
                                >
                                  EDIT
                                </Button>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>

                        <TableFooter>
                          <TableRow>
                            <TablePagination
                              rowsPerPageOptions={[5, 10, 25]}
                              SelectProps={{ fullWidth: false }}
                              count={totalCount}
                              rowsPerPage={parameters.pageSize}
                              page={parameters.page}
                              onPageChange={(_, page) => {
                                setParameters({
                                  ...parameters,
                                  page
                                })
                              }}
                              onRowsPerPageChange={e => {
                                setParameters({
                                  ...parameters,
                                  page: 0,
                                  pageSize: e.target.value as unknown as number
                                })
                              }}
                            />
                          </TableRow>
                        </TableFooter>
                      </Table>
                    </TableContainer>
                  </Table>
                </Box>
              </ListBody>
            </Grid>

            <Grid item xs={12}>
              <ExplanationAccordion variant='outlined'>
                || Vehicles appear on this to do list if “Needs Incorrect Mileage Resolution” = Populated and “Incorrect Mileage
                Resolved” = Not Populated. || The “Needs Incorrect Mileage Resolution” To Do List “Needs Incorrect Mileage
                Resolution” date will be in bold and red if current date is ≥ “Needs Incorrect Mileage Resolution” date + 2
                days. || The “Needs Incorrect Mileage Resolution” To Do List “Cluster Conversion Completed” date will be in bold
                and red if current date is ≥ Cluster Conversion Completed + 4 days. || The “Needs Incorrect Mileage Resolution”
                To Do List “Additional Programming Completed” date will be in bold and red if current date is ≥ Additional
                Programming Completed + 2 days. || The “Needs Incorrect Mileage Resolution” To Do List is sorted by “Needs
                Incorrect Mileage Resolution” oldest to newest and then by “Cluster Conversion Completed”. || Pressing “Edit” in
                the row of a vehicle takes the user to the “Vehicle Details – Cluster (tab)” for the selected vehicle. || The
                Needs Incorrect Mileage Resolution “To Do List” is a table screen intended for users on a PC. The expected use
                case for this screen is that users will see any vehicles listed here, navigate to the “Cluster Shippers”
                feature, create or select a cluster shipper, and then add vehicles needing 3rd party cluster conversion to those
                shippers. In effect, users should keep this “to do list” empty at all times. ||
              </ExplanationAccordion>
            </Grid>
          </Grid>
        </CardContent>
      </Card>
    </>
  )
}
